/**
 * Standards and base jacks.
 *
 * Ø48 tube per segment with a sleeve at each splice, a Ø38 × 160 spigot on top,
 * and a cup group every 500 mm — two collars at +200/+330 with four tabs each,
 * batched into two InstancedMeshes (T-4017306).
 */

import { Euler, InstancedMesh, Matrix4, Mesh } from "three";
import { BRACKET_STUB, CFG, KL, KR, MM, NODE, nodeY, TAPP } from "../../data/haki.ts";
import { isMounted } from "../../model/types.ts";
import { G } from "../geometries.ts";
import { M } from "../materials.ts";
import { tubeBetween, V } from "../threeHelpers.ts";
import type { RenderContext, SolvedLeg } from "./context.ts";

type CollarPoint = readonly [number, number, number];

function renderBaseJack(ctx: RenderContext, leg: SolvedLeg, extension: number): void {
  const plate = new Mesh(G.plate, M.steel);
  plate.position.set(leg.x, leg.gy + 0.006, leg.z);
  plate.castShadow = true;
  plate.receiveShadow = true;
  plate.userData = { type: "leg", key: leg.key };
  ctx.root.add(plate);
  ctx.pickElems.push(plate);

  const spindle = new Mesh(G.thread, M.thread);
  spindle.scale.y = extension;
  spindle.position.set(leg.x, leg.gy + extension / 2, leg.z);
  spindle.castShadow = true;
  ctx.root.add(spindle);

  const handleY = leg.gy + Math.min(extension * 0.55, extension - 0.03);
  const nut = new Mesh(G.collar, M.steel);
  nut.scale.set(1.55, 0.8, 1.55);
  nut.position.set(leg.x, handleY, leg.z);
  ctx.root.add(nut);
  ctx.root.add(
    tubeBetween(V(leg.x - 0.07, handleY, leg.z), V(leg.x + 0.07, handleY, leg.z), G.tube15, M.steel),
  );

  ctx.bom.add("Base", "Base jack BS 55–570", CFG.jack.art, CFG.jack.kg);
}

/** Two collars per node, each with four cup tabs — one InstancedMesh for each. */
function renderCupGroups(ctx: RenderContext, points: CollarPoint[]): void {
  if (points.length === 0) {
    return;
  }

  const rings = new InstancedMesh(G.collar, M.cast, points.length);
  const tabs = new InstancedMesh(G.tab, M.cast, points.length * 2);
  const matrix = new Matrix4();
  const euler = new Euler();

  points.forEach((point, index) => {
    rings.setMatrixAt(index, matrix.makeTranslation(point[0], point[1], point[2]));
    for (const [slot, angle] of [
      [0, 0],
      [1, Math.PI / 2],
    ] as const) {
      matrix.makeRotationFromEuler(euler.set(0, angle, 0));
      matrix.setPosition(point[0], point[1], point[2]);
      tabs.setMatrixAt(index * 2 + slot, matrix);
    }
  });

  rings.castShadow = true;
  tabs.castShadow = true;
  ctx.root.add(rings, tabs);
}

/** The segment tubes, with a sleeve at each splice. Returns the top of the stack. */
function renderSegments(ctx: RenderContext, leg: SolvedLeg, bottom: number, topIndex: number): number {
  const tubeMaterial = ctx.selectedLegKey === leg.key ? M.sel : M.alu;
  let y = bottom;

  leg.segments.forEach((mm, index) => {
    const length = mm * MM;
    const tube = new Mesh(G.tube48, tubeMaterial);
    tube.scale.y = length;
    tube.position.set(leg.x, y + length / 2, leg.z);
    tube.castShadow = true;
    tube.userData = { type: "leg", key: leg.key, top: index === topIndex };
    ctx.root.add(tube);
    ctx.pickElems.push(tube);

    if (index < leg.segments.length - 1) {
      const sleeve = new Mesh(G.sleeve, M.aluD);
      sleeve.position.set(leg.x, y + length, leg.z);
      sleeve.castShadow = true;
      ctx.root.add(sleeve);
    }
    y += length;
  });

  return y;
}

function renderSpigot(ctx: RenderContext, leg: SolvedLeg, y: number, top: boolean): void {
  const spigot = new Mesh(G.cyl, M.aluD);
  spigot.scale.set(0.019, TAPP, 0.019);
  spigot.position.set(leg.x, y + TAPP / 2, leg.z);
  spigot.castShadow = true;
  spigot.userData = { type: "leg", key: leg.key, top };
  ctx.root.add(spigot);
  ctx.pickElems.push(spigot);
}

function bookSegments(ctx: RenderContext, leg: SolvedLeg): void {
  for (const mm of leg.segments) {
    const art = CFG.spire.art[mm];
    const kg = CFG.spire.kg[mm];
    if (art !== undefined && kg !== undefined) {
      ctx.bom.add("Standards", `Standard S4 AL ${mm}`, art, kg);
    }
  }
}

export function renderStandards(ctx: RenderContext): void {
  const collarPoints: CollarPoint[] = [];

  for (const leg of ctx.legs) {
    // A standard on a bracket has no jack; it starts above the bracket's stub.
    const mounted = isMounted(leg);
    const extension = mounted ? 0 : nodeY(leg.baseP) - leg.gy;
    if (!mounted) {
      renderBaseJack(ctx, leg, extension);
    }

    // Only a stacked standard has a piece that can be taken off on its own —
    // except on a bracket, where every segment can come off down to the stub.
    const topIndex = leg.segments.length > (mounted ? 0 : 1) ? leg.segments.length - 1 : -1;
    const bottom = mounted ? nodeY(leg.baseP) + BRACKET_STUB : leg.gy + extension;
    const top = renderSegments(ctx, leg, bottom, topIndex);

    // A bare bracket stub shows the bracket's own spigot instead.
    if (!mounted || leg.segments.length > 0) {
      renderSpigot(ctx, leg, top, topIndex >= 0);
    }

    for (let cupY = nodeY(leg.baseP); cupY <= top - KR - 0.02; cupY += NODE) {
      for (const offset of [KL, KR]) {
        collarPoints.push([leg.x, cupY + offset, leg.z]);
      }
    }

    bookSegments(ctx, leg);
  }

  renderCupGroups(ctx, collarPoints);
}
